import { FormGroup } from '@angular/forms';
import { slugify } from '../validators/slug.validator';
import {
  MulesoftApplicationDTO,
  MulesoftInfrastructureDTO,
  MulesoftKnowledgeDTO,
  MulesoftPlatformDTO,
  MulesoftServiceDTO,
} from '../models/mulesoft';
import { MulesoftKnowledgeContent } from '../models/mulesoft-offering';
import {
  OfferingAddonDetail,
  OfferingFeature,
  OfferingCapability,
  OfferingUseCase,
  OfferingQualificationDocumentation,
  OfferingAvailableVersion,
  OfferingSupportChannel,
  OfferingSupportHour,
  OfferingEnvironment,
  OfferingAgent,
  OfferingCommunity,
  OfferingHosting,
  OfferingOnboardingStep,
  OfferingServiceAndRequest,
  OfferingRelatedTechnology,
} from '../models/offering';

export function buildMulesoftApplicationDTO(
  id: string,
  knowledge: string,
  formOverview: FormGroup,
  formDetails: FormGroup,
  formFunctionality: FormGroup,
  formPermissions: FormGroup,
  publish: boolean,
): MulesoftApplicationDTO {
  const overview = formOverview.getRawValue();
  const details = formDetails.getRawValue();
  const functionality = formFunctionality.getRawValue();
  const permissions = formPermissions.getRawValue();

  const displayName = overview.displayName || '';
  const slug = slugify(displayName);

  const isPerUserPricingAvailable =
    details.isPerUserPricingAvailable === true ||
    details.isPerUserPricingAvailable === 'true';
  const isSingleSignOnSupported =
    details.isSingleSignOnSupported === true ||
    details.isSingleSignOnSupported === 'true';
  const isSupported =
    details.isSupported === true || details.isSupported === 'true';
  const isAdditionalCostAvailable =
    details.isAdditionalCostConsiderationsAvailable === true ||
    details.isAdditionalCostConsiderationsAvailable === 'true';
  const isOtherPricingAvailable =
    details.isOtherPricingModelsAvailable === true ||
    details.isOtherPricingModelsAvailable === 'true';

  const offering: Record<string, unknown> = {
    id: id.toString(),
    published: publish ? 1 : 0,
    offeringName: displayName,
    displayName: displayName,
    description: overview.description || '',
    slug: slug,
    knowledge: knowledge,
    gxP: details.gxp === true || details.gxp === 'true',
    isSupported,

    ...(overview.approvalStatus && { approvalStatus: overview.approvalStatus }),
    ...(overview.successor && { successor: overview.successor }),
    ...(details.licenseCostResponsibility && {
      licenseCostResponsibility: details.licenseCostResponsibility,
    }),

    cost: {
      isPerUserPricingAvailable,
      ...(isPerUserPricingAvailable && {
        perUserCost: {
          perUserCostValue:
            details.perUserCostValue != null && details.perUserCostValue !== ''
              ? Number.parseFloat(details.perUserCostValue)
              : 0,
          ...(details.perUserCostCurrency && {
            perUserCostCurrency: details.perUserCostCurrency,
          }),
          ...(details.perUserCostFrequency && {
            perUserCostFrequency: details.perUserCostFrequency,
          }),
        },
      }),
    },

    additionalCost: {
      isAvailable: isAdditionalCostAvailable,
      ...(isAdditionalCostAvailable &&
        details.additionalCostConsiderations && {
          additionalCostConsiderations: details.additionalCostConsiderations,
        }),
    },

    otherPricingModels: {
      isAvailable: isOtherPricingAvailable,
      ...(isOtherPricingAvailable &&
        details.otherPricingModels && { details: details.otherPricingModels }),
    },

    singleSignOn: {
      isSingleSignOnSupported,
      ...(isSingleSignOnSupported &&
        details.singleSignOnProviders && {
          singleSignOnProviders: details.singleSignOnProviders,
        }),
    },

    ...(details.classificationLevel && {
      classification: {
        classificationLevel: details.classificationLevel,
        ...(details.classificationRetentionPeriod && {
          classificationRetentionPeriod: String(
            details.classificationRetentionPeriod,
          ),
        }),
        ...(details.classificationUrl && {
          classificationurl: details.classificationUrl,
        }),
      },
    }),

    ...(details.systemRiskAssessmentName && {
      systemRiskAssessment: {
        systemRiskAssessmentName: details.systemRiskAssessmentName,
      },
    }),

    contentOwnership: {
      ...(permissions.ownerEmail && { ownerEmail: permissions.ownerEmail }),
      ...(permissions.deputyEmail && { deputyEmail: permissions.deputyEmail }),
      ...(permissions.authorEmails?.length > 0 && {
        authorEmails: permissions.authorEmails,
      }),
    },
  };

  if (functionality.features?.length > 0) {
    const validFeatures = functionality.features
      .filter(
        (feature: { name: string; description: string; benefit: string }) =>
          feature.name && feature.description,
      )
      .map(
        (feature: { name: string; description: string; benefit: string }) => ({
          name: feature.name,
          description: feature.description,
          ...(feature.benefit && { benefit: feature.benefit }),
        }),
      );
    if (validFeatures.length > 0) {
      offering['features'] = validFeatures;
    }
  }

  if (functionality.useCases?.length > 0) {
    const validUseCases = functionality.useCases
      .filter(
        (useCase: { name: string; description: string }) =>
          useCase.name || useCase.description,
      )
      .map((useCase: { name: string; description: string }) => ({
        ...(useCase.name && { name: useCase.name }),
        ...(useCase.description && { description: useCase.description }),
      }));
    if (validUseCases.length > 0) {
      offering['useCases'] = validUseCases;
    }
  }

  if (
    Array.isArray(overview.similarOfferings) &&
    overview.similarOfferings.length > 0
  ) {
    offering['similarOfferings'] = overview.similarOfferings.map(
      (o: { uuid: string }) => o.uuid,
    );
  }

  if (details.installationInformation?.length > 0) {
    const validInstallations = details.installationInformation
      .filter(
        (installation: {
          applicationType: string;
          instructions: string;
          url: string;
        }) =>
          installation.applicationType &&
          installation.instructions &&
          installation.url,
      )
      .map(
        (installation: {
          applicationType: string;
          instructions: string;
          url: string;
        }) => ({
          applicationType: installation.applicationType,
          instructions: installation.instructions,
          url: installation.url,
        }),
      );
    if (validInstallations.length > 0) {
      offering['installationInformation'] = validInstallations;
    }
  }

  if (details.communities?.length > 0) {
    const validCommunities = details.communities
      .filter(
        (c: { title: string; description: string; url: string }) =>
          c.title || c.description || c.url,
      )
      .map((c: { title: string; description: string; url: string }) => ({
        ...(c.title && { title: c.title }),
        ...(c.description && { description: c.description }),
        ...(c.url && { url: c.url }),
      }));
    if (validCommunities.length > 0) {
      offering['community'] = validCommunities;
    }
  }

  if (details.supportChannels?.length > 0) {
    offering['supportChannels'] = details.supportChannels;
  }

  if (details.supportHours?.length > 0) {
    offering['supportHours'] = details.supportHours;
  }

  if (details.hosting?.length > 0) {
    const validHosting = details.hosting
      .filter(
        (h: { name: string; type: string; managed: string }) =>
          h.name || h.type || h.managed,
      )
      .map((h: { name: string; type: string; managed: string }) => ({
        ...(h.name && { hostingName: h.name }),
        ...(h.type && { hostingType: h.type }),
        ...(h.managed && { hostingManaged: h.managed }),
      }));
    if (validHosting.length > 0) {
      offering['hosting'] = validHosting;
    }
  }

  if (details.agents?.length > 0) {
    const validAgents = details.agents
      .filter(
        (agent: { name: string; url: string }) => agent.name || agent.url,
      )
      .map((agent: { name: string; url: string }) => ({
        ...(agent.name && { agentName: agent.name }),
        ...(agent.url && { agentUrl: agent.url }),
      }));
    if (validAgents.length > 0) {
      offering['agent'] = validAgents;
    }
  }

  if (details.servicesAndRequests?.length > 0) {
    const validRequests = details.servicesAndRequests
      .filter(
        (request: {
          id: string;
          type: string;
          title: string;
          description: string;
          destinationValue: string;
        }) =>
          request.id ||
          request.type ||
          request.title ||
          request.description ||
          request.destinationValue,
      )
      .map(
        (request: {
          id: string;
          type: string;
          title: string;
          description: string;
          destinationValue: string;
        }) => ({
          ...(request.id && { id: request.id }),
          ...(request.type && { type: request.type }),
          ...(request.title && { title: request.title }),
          ...(request.description && { description: request.description }),
          ...(request.destinationValue && {
            destinationValue: request.destinationValue,
          }),
        }),
      );
    if (validRequests.length > 0) {
      offering['servicesAndRequests'] = validRequests;
    }
  }

  return {
    offering: offering,
  } as MulesoftApplicationDTO;
}

export function buildMulesoftKnowledgeDTO(
  knowledge: MulesoftKnowledgeContent,
  formKnowledge: FormGroup,
  offeringUuid: string,
  offeringName: string,
  publish: boolean,
): MulesoftKnowledgeDTO {
  const form = formKnowledge.getRawValue();
  const knowledgeName = offeringName ? `${offeringName} knowledge` : '';
  const slug = slugify(knowledgeName);

  const knowledgePayload: Record<string, unknown> = {
    id: knowledge.id || '',
    knowledgeName: knowledgeName,
    slug: slug,
    offering: offeringUuid,
    published: publish ? 1 : 0,
    // Section visibility flags
    userInstructionsVisible: form.userInstructionsVisible || false,
    bestPracticesVisible: form.bestPracticesVisible || false,
    learningResourcesVisible: form.learningResourcesVisible || false,
    technicalResourcesVisible: form.technicalResourcesVisible || false,
    integrationsVisible: form.integrationsVisible || false,
    faqVisible: form.faqVisible || false,
    additionalResourcesVisible: form.additionalResourcesVisible || false,
    successStoriesVisible: form.successStoriesVisible || false,
    releaseNotesVisible: form.releaseNotesVisible || false,
  };

  // Add userInstructions section only if there's data
  if (form.userInstructionLongDescription) {
    knowledgePayload['userInstructionLongDescription'] =
      form.userInstructionLongDescription;
  }
  if (form.userInstructions?.length > 0) {
    knowledgePayload['userInstructions'] = form.userInstructions.map(
      (item: { title: string; description: string; published: boolean }) => ({
        title: item.title || '',
        description: item.description || '',
        published: item.published || false,
      }),
    );
  }

  // Add learningResources only if there's data
  if (form.learningResources?.length > 0) {
    knowledgePayload['learningResources'] = form.learningResources.map(
      (item: { title: string; url: string; published: boolean }) => ({
        title: item.title || '',
        url: item.url || '',
        published: item.published || false,
      }),
    );
  }

  // Add technicalResources only if there's data
  if (form.technicalResources?.length > 0) {
    knowledgePayload['technicalResources'] = form.technicalResources.map(
      (item: { title: string; url: string; published: boolean }) => ({
        title: item.title || '',
        url: item.url || '',
        published: item.published || false,
      }),
    );
  }

  // Add faq only if there's data
  if (form.faq?.length > 0) {
    knowledgePayload['faq'] = form.faq.map(
      (item: { question: string; answer: string; published: boolean }) => ({
        question: item.question || '',
        answer: item.answer || '',
        published: item.published || false,
      }),
    );
  }

  // Add successStory only if there's data
  if (form.successStories?.length > 0) {
    knowledgePayload['successStories'] = form.successStories.map(
      (item: { title: string; description: string; published: boolean }) => ({
        title: item.title || '',
        description: item.description || '',
        published: item.published || false,
      }),
    );
  }

  // Add releaseNote only if there's data
  if (form.releaseNotes?.length > 0) {
    knowledgePayload['releaseNotes'] = form.releaseNotes.map(
      (item: {
        header: string;
        subheader: string;
        description: string;
        url: string;
        date: string;
        published: boolean;
      }) => ({
        header: item.header || '',
        date: item.date || '',
        subheader: item.subheader || '',
        description: item.description || '',
        url: item.url || '',
        published: item.published || false,
      }),
    );
  }

  // Add additionalResources only if there's data
  if (form.additionalResources?.length > 0) {
    knowledgePayload['additionalResources'] = form.additionalResources.map(
      (item: { title: string; url: string; published: boolean }) => ({
        title: item.title || '',
        url: item.url || '',
        published: item.published || false,
      }),
    );
  }

  // Add integration section only if there's data
  if (form.integrationLongDescription) {
    knowledgePayload['integrationLongDescription'] =
      form.integrationLongDescription;
  }
  if (form.integrations?.length > 0) {
    knowledgePayload['integrations'] = form.integrations.map(
      (item: {
        title: string;
        description: string;
        urlName: string;
        url: string;
        published: boolean;
      }) => ({
        title: item.title || '',
        longDescription: form.integrationLongDescription || '',
        description: item.description || '',
        urlName: item.urlName || '',
        url: item.url || '',
        published: item.published || false,
      }),
    );
  }

  // Add bestPractice only if there's data
  if (form.bestPractices?.length > 0) {
    knowledgePayload['bestPractices'] = form.bestPractices.map(
      (item: { title: string; description: string; published: boolean }) => ({
        title: item.title || '',
        description: item.description || '',
        published: item.published || false,
      }),
    );
  }

  return {
    knowledge: knowledgePayload,
  } as MulesoftKnowledgeDTO;
}

export function buildMulesoftInfrastructureDTO(
  id: string,
  knowledge: string,
  formOverview: FormGroup,
  formDetails: FormGroup,
  formFunctionality: FormGroup,
  formPermissions: FormGroup,
  publish: boolean,
): MulesoftInfrastructureDTO {
  const overview = formOverview.getRawValue();
  const details = formDetails.getRawValue();
  const functionality = formFunctionality.getRawValue();
  const permissions = formPermissions.getRawValue();

  const displayName = overview.displayName || '';
  const slug = slugify(displayName);

  const offering: Record<string, unknown> = {
    id: id.toString(),
    published: publish ? 1 : 0,
    offeringName: displayName,
    displayName: displayName,
    description: overview.description || '',
    slug: slug,
    knowledge: knowledge,

    ...(overview.approvalStatus && {
      approvalStatus: overview.approvalStatus,
    }),
    ...(overview.successor && {
      successor: overview.successor,
    }),
    ...(details.qualificationStatus && {
      qualificationStatus: details.qualificationStatus,
    }),

    cost: {
      isPerUserPricingAvailable: details.isPerUserPricingAvailable || false,
      ...(details.isPerUserPricingAvailable && {
        perUserCost: {
          perUserCostValue: details.perUserCostValue
            ? Number.parseFloat(details.perUserCostValue)
            : 0,
          ...(details.perUserCostCurrency && {
            perUserCostCurrency: details.perUserCostCurrency,
          }),
          ...(details.perUserCostFrequency && {
            perUserCostFrequency: details.perUserCostFrequency,
          }),
        },
      }),
    },

    additionalCost: {
      isAvailable: details.isAdditionalCostConsiderationsAvailable || false,
      ...(details.additionalCostConsiderations && {
        additionalCostConsiderations: details.additionalCostConsiderations,
      }),
    },
    otherPricingModels: {
      isAvailable: details.isOtherPricingModelsAvailable || false,
      ...(details.otherPricingModels && {
        details: details.otherPricingModels,
      }),
    },

    ...(details.developmentCostResponsibility && {
      developmentCostResponsibility: {
        responsibility: details.developmentCostResponsibility,
        otherDetails: details.developmentCostResponsibilityOtherDetails,
      },
    }),

    ...(details.supportMaintenanceCostResponsibility && {
      supportMaintenanceCostResponsibility: {
        responsibility: details.supportMaintenanceCostResponsibility,
        otherDetails: details.supportMaintenanceCostResponsibilityOtherDetails,
      },
    }),

    ...(details.licenseCostResponsibility && {
      licenseCostResponsibility: details.licenseCostResponsibility,
    }),

    isSupported: details.isSupported || false,

    singleSignOn: {
      isSingleSignOnSupported: details.isSingleSignOnSupported || false,
      ...(details.singleSignOnProviders?.length > 0 && {
        singleSignOnProviders: details.singleSignOnProviders,
      }),
    },

    ...(details.classificationLevel && {
      classification: {
        classificationLevel: details.classificationLevel,
      },
    }),

    ...(details.systemRiskAssessmentName && {
      systemRiskAssessment: {
        systemRiskAssessmentName: details.systemRiskAssessmentName,
      },
    }),

    contentOwnership: {
      ...(permissions.ownerEmail && { ownerEmail: permissions.ownerEmail }),
      ...(permissions.deputyEmail && { deputyEmail: permissions.deputyEmail }),
      ...(permissions.authorEmails?.length > 0 && {
        authorEmails: permissions.authorEmails,
      }),
    },
  };

  if (
    Array.isArray(overview.similarOfferings) &&
    overview.similarOfferings.length > 0
  ) {
    offering['similarOfferings'] = overview.similarOfferings.map(
      (o: { uuid: string }) => o.uuid,
    );
  }

  if (functionality.features?.length > 0) {
    const validFeatures = functionality.features
      .filter(
        (feature: OfferingFeature) => feature.name && feature.description,
      )
      .map((feature: OfferingFeature) => ({
        name: feature.name,
        description: feature.description,
        ...(feature.benefit && { benefit: feature.benefit }),
      }));
    if (validFeatures.length > 0) {
      offering['features'] = validFeatures;
    }
  }

  if (functionality.capabilities?.length > 0) {
    const validCapabilities = functionality.capabilities
      .filter(
        (capability: OfferingCapability) =>
          capability.name || capability.description,
      )
      .map((capability: OfferingCapability) => ({
        ...(capability.name && { name: capability.name }),
        ...(capability.description && { description: capability.description }),
        ...(capability.benefit && { benefit: capability.benefit }),
      }));
    if (validCapabilities.length > 0) {
      offering['capabilities'] = validCapabilities;
    }
  }

  if (functionality.useCases?.length > 0) {
    const validUseCases = functionality.useCases
      .filter(
        (useCase: OfferingUseCase) => useCase.name || useCase.description,
      )
      .map((useCase: OfferingUseCase) => ({
        ...(useCase.name && { name: useCase.name }),
        ...(useCase.description && { description: useCase.description }),
      }));
    if (validUseCases.length > 0) {
      offering['useCases'] = validUseCases;
    }
  }

  if (details.qualificationDocumentation?.length > 0) {
    const validDocs = details.qualificationDocumentation
      .filter(
        (qd: OfferingQualificationDocumentation) => qd.name || qd.url,
      )
      .map((qd: OfferingQualificationDocumentation) => ({
        ...(qd.name && { documentName: qd.name }),
        ...(qd.url && { documentUrl: qd.url }),
      }));
    if (validDocs.length > 0) {
      offering['qualificationDocumentation'] = validDocs;
    }
  }

  if (details.availableVersions?.length > 0) {
    const validVersions = details.availableVersions
      .filter(
        (av: OfferingAvailableVersion) =>
          av.version || av.status || av.compatibility,
      )
      .map((av: OfferingAvailableVersion) => ({
        ...(av.version && { version: av.version }),
        ...(av.status && { status: av.status }),
        ...(av.compatibility && { compatibility: av.compatibility }),
        ...(av.releaseDate && { releaseDate: av.releaseDate }),
        ...(av.endOfLifeDate && { endOfLifeDate: av.endOfLifeDate }),
        ...(av.additionalInfo && { additionalInfo: av.additionalInfo }),
      }));
    if (validVersions.length > 0) {
      offering['availableVersions'] = validVersions;
    }
  }

  if (details.supportChannels?.length > 0) {
    const validChannels = details.supportChannels
      .filter(
        (sc: OfferingSupportChannel) =>
          sc.channelType || sc.destinationValue,
      )
      .map((sc: OfferingSupportChannel) => ({
        ...(sc.channelType && { channelType: sc.channelType }),
        ...(sc.destinationValue && { destinationValue: sc.destinationValue }),
        ...(sc.description && { description: sc.description }),
        ...(sc.serviceDeskName && { serviceDeskName: sc.serviceDeskName }),
      }));
    if (validChannels.length > 0) {
      offering['supportChannels'] = validChannels;
    }
  }

  if (details.supportHours?.length > 0) {
    const validHours = details.supportHours
      .filter(
        (sh: OfferingSupportHour) =>
          (sh.days?.length > 0) || sh.startTime || sh.endTime,
      )
      .map((sh: OfferingSupportHour) => ({
        ...(sh.days?.length > 0 && { days: sh.days }),
        ...(sh.startTime && { startTime: sh.startTime }),
        ...(sh.endTime && { endTime: sh.endTime }),
        ...(sh.timezone && { timezone: sh.timezone }),
        ...(sh.responseTime && { responseTime: sh.responseTime }),
      }));
    if (validHours.length > 0) {
      offering['supportHours'] = validHours;
    }
  }

  if (details.environments?.length > 0) {
    const validEnvs = details.environments
      .filter(
        (env: OfferingEnvironment) => env.name || env.url,
      )
      .map((env: OfferingEnvironment) => ({
        ...(env.name && { environmentName: env.name }),
        ...(env.url && { environmentUrl: env.url }),
        ...(env.serviceLevelAgreement && {
          serviceLevelAgreement: env.serviceLevelAgreement,
        }),
      }));
    if (validEnvs.length > 0) {
      offering['environment'] = validEnvs;
    }
  }

  if (details.agents?.length > 0) {
    const validAgents = details.agents
      .filter((agent: OfferingAgent) => agent.name || agent.url)
      .map((agent: OfferingAgent) => ({
        ...(agent.name && { agentName: agent.name }),
        ...(agent.url && { agentUrl: agent.url }),
      }));
    if (validAgents.length > 0) {
      offering['agent'] = validAgents;
    }
  }

  if (details.communities?.length > 0) {
    const validCommunities = details.communities
      .filter(
        (c: OfferingCommunity) => c.title || c.description || c.url,
      )
      .map((c: OfferingCommunity) => ({
        ...(c.title && { title: c.title }),
        ...(c.description && { description: c.description }),
        ...(c.url && { url: c.url }),
      }));
    if (validCommunities.length > 0) {
      offering['community'] = validCommunities;
    }
  }

  if (details.hosting?.length > 0) {
    const validHosting = details.hosting
      .filter(
        (h: OfferingHosting) => h.name || h.type || h.managed,
      )
      .map((h: OfferingHosting) => ({
        ...(h.name && { hostingName: h.name }),
        ...(h.type && { hostingType: h.type }),
        ...(h.managed && { hostingManaged: h.managed }),
      }));
    if (validHosting.length > 0) {
      offering['hosting'] = validHosting;
    }
  }

  if (details.servicesAndRequests?.length > 0) {
    const validRequests = details.servicesAndRequests
      .filter(
        (request: OfferingServiceAndRequest) =>
          request.type || request.title || request.description,
      )
      .map((request: OfferingServiceAndRequest) => ({
        ...(request.id && { id: request.id }),
        ...(request.type && { type: request.type }),
        ...(request.title && { title: request.title }),
        ...(request.description && { description: request.description }),
        ...(request.destinationValue && {
          destinationValue: request.destinationValue,
        }),
      }));
    if (validRequests.length > 0) {
      offering['servicesAndRequests'] = validRequests;
    }
  }

  return {
    offering,
  } as MulesoftInfrastructureDTO;
}

export function buildMulesoftPlatformDTO(
  id: string,
  knowledge: string,
  formOverview: FormGroup,
  formDetails: FormGroup,
  formFunctionality: FormGroup,
  formPermissions: FormGroup,
  publish: boolean,
): MulesoftPlatformDTO {
  const overview = formOverview.getRawValue();
  const details = formDetails.getRawValue();
  const functionality = formFunctionality.getRawValue();
  const permissions = formPermissions.getRawValue();

  const displayName = overview.displayName || '';
  const slug = slugify(displayName);

  const offering: Record<string, unknown> = {
    id: id.toString(),
    published: publish ? 1 : 0,
    offeringName: displayName,
    displayName: displayName,
    description: overview.description || '',
    slug: slug,
    knowledge: knowledge,

    ...(overview.approvalStatus && {
      approvalStatus: overview.approvalStatus,
    }),
    ...(overview.successor && {
      successor: overview.successor,
    }),

    ...(overview.relationshipType && {
        offeringRelationship: {
          relationshipType: overview.relationshipType,
          targetOfferingId: overview.relationshipTargetOfferingId || '',
        },
      }),

    ...((details.vendorName || details.vendorSupport || details.vendorUrl) && {
      vendorInfo: {
        ...(details.vendorName && { vendorName: details.vendorName }),
        ...(details.vendorSupport && { vendorSupport: details.vendorSupport }),
        ...(details.vendorUrl && { vendorUrl: details.vendorUrl }),
      },
    }),

    ...(details.relatedTechnologies?.length > 0 && {
      relatedTechnologies: details.relatedTechnologies.map(
        (technology: { targetId: string; description: string }) => ({
          targetId: technology.targetId,
          description: technology.description ?? '',
        }),
      ),
    }),

    ...(details.isExternalUseAllowed !== null &&
      details.isExternalUseAllowed !== undefined && {
        isExternalUseAllowed: details.isExternalUseAllowed,
      }),

    isSraRequired: details.isSraRequired || false,

    ...((details.supportedDataClassificationLevel ||
      details.supportedDataClassificationAdditionalInformation) && {
      supportedDataClassification: {
        ...(details.supportedDataClassificationLevel && {
          level: details.supportedDataClassificationLevel,
        }),
        ...(details.supportedDataClassificationAdditionalInformation && {
          additionalInformation:
            details.supportedDataClassificationAdditionalInformation,
        }),
      },
    }),

    hasAddons: details.hasAddons || false,

    cost: {
      isPerUserPricingAvailable: details.isPerUserPricingAvailable || false,
      ...(details.isPerUserPricingAvailable && {
        perUserCost: {
          perUserCostValue: details.perUserCostValue
            ? Number.parseFloat(details.perUserCostValue)
            : 0,
          ...(details.perUserCostCurrency && {
            perUserCostCurrency: details.perUserCostCurrency,
          }),
          ...(details.perUserCostFrequency && {
            perUserCostFrequency: details.perUserCostFrequency,
          }),
        },
      }),
    },

    additionalCost: {
      isAvailable: details.isAdditionalCostConsiderationsAvailable || false,
      ...(details.additionalCostConsiderations && {
        additionalCostConsiderations: details.additionalCostConsiderations,
      }),
    },
    otherPricingModels: {
      isAvailable: details.isOtherPricingModelsAvailable || false,
      ...(details.otherPricingModels && {
        details: details.otherPricingModels,
      }),
    },

    ...(details.developmentCostResponsibility && {
      developmentCostResponsibility: {
        responsibility: details.developmentCostResponsibility,
        otherDetails: details.developmentCostResponsibilityOtherDetails,
      },
    }),

    ...(details.supportMaintenanceCostResponsibility && {
      supportMaintenanceCostResponsibility: {
        responsibility: details.supportMaintenanceCostResponsibility,
        otherDetails: details.supportMaintenanceCostResponsibilityOtherDetails,
      },
    }),

    ...(details.licenseCostResponsibility && {
      licenseCostResponsibility: details.licenseCostResponsibility,
    }),

    isSupported: details.isSupported || false,

    singleSignOn: {
      isSingleSignOnSupported: details.isSingleSignOnSupported || false,
      ...(details.singleSignOnProviders?.length > 0 && {
        singleSignOnProviders: details.singleSignOnProviders,
      }),
    },

    ...(details.classificationLevel && {
      classification: {
        classificationLevel: details.classificationLevel,
      },
    }),

    ...(details.systemRiskAssessmentName && {
      systemRiskAssessment: {
        systemRiskAssessmentName: details.systemRiskAssessmentName,
      },
    }),

    contentOwnership: {
      ...(permissions.ownerEmail && { ownerEmail: permissions.ownerEmail }),
      ...(permissions.deputyEmail && { deputyEmail: permissions.deputyEmail }),
      ...(permissions.authorEmails?.length > 0 && {
        authorEmails: permissions.authorEmails,
      }),
    },
  };

  if (
    Array.isArray(overview.similarOfferings) &&
    overview.similarOfferings.length > 0
  ) {
    offering['similarOfferings'] = overview.similarOfferings.map(
      (o: { uuid: string }) => o.uuid,
    );
  }

  if (functionality.features?.length > 0) {
    const validFeatures = functionality.features
      .filter(
        (feature: OfferingFeature) => feature.name && feature.description,
      )
      .map((feature: OfferingFeature) => ({
        name: feature.name,
        description: feature.description,
        ...(feature.benefit && { benefit: feature.benefit }),
      }));
    if (validFeatures.length > 0) {
      offering['features'] = validFeatures;
    }
  }

  if (functionality.capabilities?.length > 0) {
    const validCapabilities = functionality.capabilities
      .filter(
        (capability: OfferingCapability) =>
          capability.name || capability.description,
      )
      .map((capability: OfferingCapability) => ({
        ...(capability.name && { name: capability.name }),
        ...(capability.description && { description: capability.description }),
        ...(capability.benefit && { benefit: capability.benefit }),
      }));
    if (validCapabilities.length > 0) {
      offering['capabilities'] = validCapabilities;
    }
  }

  if (functionality.useCases?.length > 0) {
    const validUseCases = functionality.useCases
      .filter(
        (useCase: OfferingUseCase) => useCase.name || useCase.description,
      )
      .map((useCase: OfferingUseCase) => ({
        ...(useCase.name && { name: useCase.name }),
        ...(useCase.description && { description: useCase.description }),
      }));
    if (validUseCases.length > 0) {
      offering['useCases'] = validUseCases;
    }
  }

  if (details.availableVersions?.length > 0) {
    const validVersions = details.availableVersions
      .filter(
        (av: OfferingAvailableVersion) =>
          av.version || av.status || av.compatibility,
      )
      .map((av: OfferingAvailableVersion) => ({
        ...(av.version && { version: av.version }),
        ...(av.status && { status: av.status }),
        ...(av.compatibility && { compatibility: av.compatibility }),
        ...(av.releaseDate && { releaseDate: av.releaseDate }),
        ...(av.endOfLifeDate && { endOfLifeDate: av.endOfLifeDate }),
        ...(av.additionalInfo && { additionalInfo: av.additionalInfo }),
      }));
    if (validVersions.length > 0) {
      offering['availableVersions'] = validVersions;
    }
  }

  if (details.supportChannels?.length > 0) {
    const validChannels = details.supportChannels
      .filter(
        (sc: OfferingSupportChannel) =>
          sc.channelType || sc.destinationValue,
      )
      .map((sc: OfferingSupportChannel) => ({
        ...(sc.channelType && { channelType: sc.channelType }),
        ...(sc.destinationValue && { destinationValue: sc.destinationValue }),
        ...(sc.description && { description: sc.description }),
        ...(sc.serviceDeskName && { serviceDeskName: sc.serviceDeskName }),
      }));
    if (validChannels.length > 0) {
      offering['supportChannels'] = validChannels;
    }
  }

  if (details.supportHours?.length > 0) {
    const validHours = details.supportHours
      .filter(
        (sh: OfferingSupportHour) =>
          (sh.days?.length > 0) || sh.startTime || sh.endTime,
      )
      .map((sh: OfferingSupportHour) => ({
        ...(sh.days?.length > 0 && { days: sh.days }),
        ...(sh.startTime && { startTime: sh.startTime }),
        ...(sh.endTime && { endTime: sh.endTime }),
        ...(sh.timezone && { timezone: sh.timezone }),
        ...(sh.responseTime && { responseTime: sh.responseTime }),
      }));
    if (validHours.length > 0) {
      offering['supportHours'] = validHours;
    }
  }

  if (details.environments?.length > 0) {
    const validEnvs = details.environments
      .filter(
        (env: OfferingEnvironment) => env.name || env.url,
      )
      .map((env: OfferingEnvironment) => ({
        ...(env.name && { environmentName: env.name }),
        ...(env.url && { environmentUrl: env.url }),
        ...(env.serviceLevelAgreement && {
          serviceLevelAgreement: env.serviceLevelAgreement,
        }),
      }));
    if (validEnvs.length > 0) {
      offering['environment'] = validEnvs;
    }
  }

  if (details.agents?.length > 0) {
    const validAgents = details.agents
      .filter((agent: OfferingAgent) => agent.name || agent.url)
      .map((agent: OfferingAgent) => ({
        ...(agent.name && { agentName: agent.name }),
        ...(agent.url && { agentUrl: agent.url }),
      }));
    if (validAgents.length > 0) {
      offering['agent'] = validAgents;
    }
  }

  if (details.communities?.length > 0) {
    const validCommunities = details.communities
      .filter(
        (c: OfferingCommunity) => c.title || c.description || c.url,
      )
      .map((c: OfferingCommunity) => ({
        ...(c.title && { title: c.title }),
        ...(c.description && { description: c.description }),
        ...(c.url && { url: c.url }),
      }));
    if (validCommunities.length > 0) {
      offering['community'] = validCommunities;
    }
  }

  if (details.hosting?.length > 0) {
    const validHosting = details.hosting
      .filter(
        (h: OfferingHosting) => h.name || h.type || h.managed,
      )
      .map((h: OfferingHosting) => ({
        ...(h.name && { hostingName: h.name }),
        ...(h.type && { hostingType: h.type }),
        ...(h.managed && { hostingManaged: h.managed }),
      }));
    if (validHosting.length > 0) {
      offering['hosting'] = validHosting;
    }
  }

  if (details.servicesAndRequests?.length > 0) {
    const validRequests = details.servicesAndRequests
      .filter(
        (request: OfferingServiceAndRequest) =>
          request.type || request.title || request.description,
      )
      .map((request: OfferingServiceAndRequest) => ({
        ...(request.id && { id: request.id }),
        ...(request.type && { type: request.type }),
        ...(request.title && { title: request.title }),
        ...(request.description && { description: request.description }),
        ...(request.destinationValue && {
          destinationValue: request.destinationValue,
        }),
      }));
    if (validRequests.length > 0) {
      offering['servicesAndRequests'] = validRequests;
    }
  }

  if (
    details.onboardingAccessInstructions ||
    details.onboardingSteps?.length > 0
  ) {
    const validSteps = (details.onboardingSteps ?? []).filter(
      (step: OfferingOnboardingStep) => step.description || step.referenceUrl,
    );
    offering['onboarding'] = {
      ...(details.onboardingAccessInstructions && {
        accessInstructions: details.onboardingAccessInstructions,
      }),
      ...(validSteps.length > 0 && {
        installationSteps: validSteps.map(
          (step: OfferingOnboardingStep) => ({
            ...(step.description && { description: step.description }),
            ...(step.referenceUrl && { referenceUrl: step.referenceUrl }),
          }),
        ),
      }),
    };
  }

  if (details.addonDetails?.length > 0) {
    const validAddons = details.addonDetails
      .filter(
        (addon: OfferingAddonDetail) =>
          addon.name || addon.description || addon.url,
      )
      .map((addon: OfferingAddonDetail) => ({
        ...(addon.name && { addonName: addon.name }),
        ...(addon.description && { addonDescription: addon.description }),
        ...(addon.cost && { addonCost: addon.cost }),
        addonSupport: addon.support ?? false,
        ...(addon.url && { addonURL: addon.url }),
      }));
    if (validAddons.length > 0) {
      offering['addonDetails'] = validAddons;
    }
  }

  return {
    offering,
  } as MulesoftPlatformDTO;
}

export function buildMulesoftServiceDTO(
  id: string,
  knowledge: string,
  formOverview: FormGroup,
  formDetails: FormGroup,
  formFunctionality: FormGroup,
  formPermissions: FormGroup,
  publish: boolean,
): MulesoftServiceDTO {
  const overview = formOverview.getRawValue();
  const details = formDetails.getRawValue();
  const functionality = formFunctionality.getRawValue();
  const permissions = formPermissions.getRawValue();

  const displayName = overview.displayName || '';
  const slug = slugify(displayName);

  const offering: Record<string, unknown> = {
    id: id.toString(),
    published: publish ? 1 : 0,
    offeringName: displayName,
    displayName: displayName,
    description: overview.description || '',
    slug: slug,
    knowledge: knowledge,

    ...(overview.approvalStatus && {
      approvalStatus: overview.approvalStatus,
    }),
    ...(overview.successor && {
      successor: overview.successor,
    }),

    ...((details.vendorName || details.vendorSupport || details.vendorUrl) && {
      vendorInfo: {
        ...(details.vendorName && { vendorName: details.vendorName }),
        ...(details.vendorSupport && { vendorSupport: details.vendorSupport }),
        ...(details.vendorUrl && { vendorUrl: details.vendorUrl }),
      },
    }),

    additionalCost: {
      isAvailable: details.isAdditionalCostConsiderationsAvailable || false,
      ...(details.additionalCostConsiderations && {
        additionalCostConsiderations: details.additionalCostConsiderations,
      }),
    },
    otherPricingModels: {
      isAvailable: details.isOtherPricingModelsAvailable || false,
      ...(details.otherPricingModels && {
        details: details.otherPricingModels,
      }),
    },

    contentOwnership: {
      ...(permissions.ownerEmail && { ownerEmail: permissions.ownerEmail }),
      ...(permissions.deputyEmail && { deputyEmail: permissions.deputyEmail }),
      ...(permissions.authorEmails?.length > 0 && {
        authorEmails: permissions.authorEmails,
      }),
    },
  };

  if (
    Array.isArray(overview.similarOfferings) &&
    overview.similarOfferings.length > 0
  ) {
    offering['similarOfferings'] = overview.similarOfferings.map(
      (o: { uuid: string }) => o.uuid,
    );
  }


  if (functionality.capabilities?.length > 0) {
    const validCapabilities = functionality.capabilities
      .filter(
        (capability: OfferingCapability) =>
          capability.name || capability.description,
      )
      .map((capability: OfferingCapability) => ({
        ...(capability.name && { name: capability.name }),
        ...(capability.description && { description: capability.description }),
        ...(capability.benefit && { benefit: capability.benefit }),
      }));
    if (validCapabilities.length > 0) {
      offering['capabilities'] = validCapabilities;
    }
  }

  if (functionality.useCases?.length > 0) {
    const validUseCases = functionality.useCases
      .filter(
        (useCase: OfferingUseCase) => useCase.name || useCase.description,
      )
      .map((useCase: OfferingUseCase) => ({
        ...(useCase.name && { name: useCase.name }),
        ...(useCase.description && { description: useCase.description }),
      }));
    if (validUseCases.length > 0) {
      offering['useCases'] = validUseCases;
    }
  }

  if (details.agents?.length > 0) {
    const validAgents = details.agents
      .filter((agent: OfferingAgent) => agent.name || agent.url)
      .map((agent: OfferingAgent) => ({
        ...(agent.name && { agentName: agent.name }),
        ...(agent.url && { agentUrl: agent.url }),
      }));
    if (validAgents.length > 0) {
      offering['agent'] = validAgents;
    }
  }

  if (details.communities?.length > 0) {
    const validCommunities = details.communities
      .filter(
        (c: OfferingCommunity) => c.title || c.description || c.url,
      )
      .map((c: OfferingCommunity) => ({
        ...(c.title && { title: c.title }),
        ...(c.description && { description: c.description }),
        ...(c.url && { url: c.url }),
      }));
    if (validCommunities.length > 0) {
      offering['community'] = validCommunities;
    }
  }

  if (details.servicesAndRequests?.length > 0) {
    const validRequests = details.servicesAndRequests
      .filter(
        (request: OfferingServiceAndRequest) =>
          request.type || request.title || request.description,
      )
      .map((request: OfferingServiceAndRequest) => ({
        ...(request.id && { id: request.id }),
        ...(request.type && { type: request.type }),
        ...(request.title && { title: request.title }),
        ...(request.description && { description: request.description }),
        ...(request.destinationValue && {
          destinationValue: request.destinationValue,
        }),
      }));
    if (validRequests.length > 0) {
      offering['servicesAndRequests'] = validRequests;
    }
  }

  if (details.relatedTechnology?.length > 0) {
    const validTech = details.relatedTechnology
      .filter(
        (technology: OfferingRelatedTechnology) =>
          technology.targetId || technology.description,
      )
      .map((technology: OfferingRelatedTechnology) => ({
        ...(technology.targetId && { targetId: technology.targetId }),
        ...(technology.description && { description: technology.description }),
      }));
    if (validTech.length > 0) {
      offering['relatedTechnology'] = validTech;
    }
  }

  if (
    details.onboardingAccessInstructions ||
    details.onboardingSteps?.length > 0
  ) {
    const validSteps = (details.onboardingSteps ?? []).filter(
      (step: OfferingOnboardingStep) => step.description || step.referenceUrl,
    );
    offering['onboarding'] = {
      ...(details.onboardingAccessInstructions && {
        accessInstructions: details.onboardingAccessInstructions,
      }),
      ...(validSteps.length > 0 && {
        installationSteps: validSteps.map(
          (step: OfferingOnboardingStep) => ({
            ...(step.description && { description: step.description }),
            ...(step.referenceUrl && { referenceUrl: step.referenceUrl }),
          }),
        ),
      }),
    };
  }

  return {
    offering,
  } as MulesoftServiceDTO;
}
